import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { formatNumber } from "@/lib/format";
import type { PlatformStats } from "@/pages/home/hooks/useHomeData";

interface HeroSectionProps {
  stats: PlatformStats | null;
  loading: boolean;
}

export default function HeroSection({ stats, loading }: HeroSectionProps) {
  const { t } = useTranslation();
  const heroStats = [
    { label: t("hero.statCustomers"), value: stats?.total_customers },
    { label: t("hero.statActive"), value: stats?.active_licenses },
    { label: t("hero.statSeats"), value: stats?.online_seats },
  ];

  return (
    <section className="relative w-full overflow-hidden">
      <div className="absolute inset-0">
        <img
          src="https://readdy.ai/api/search-image?query=Abstract%20dark%20emerald%20green%20technology%20background%20with%20flowing%20geometric%20network%20lines%20and%20soft%20glowing%20nodes%20symbolizing%20secure%20software%20license%20management%2C%20deep%20charcoal%20teal%20gradient%2C%20subtle%20light%20rays%2C%20minimal%20elegant%20futuristic%20aesthetic%2C%20generous%20empty%20space%20in%20the%20center%20for%20headline%20text%2C%20high%20detail%2C%20no%20text&width=1920&height=900&seq=license-hero-01&orientation=landscape"
          alt={t("hero.imgAlt")}
          title={t("hero.imgTitle")}
          className="h-full w-full object-cover object-top"
        />
      </div>
      <div className="absolute inset-0 bg-gradient-to-b from-foreground-950/75 via-foreground-950/60 to-foreground-950/85" />

      <div className="relative mx-auto flex min-h-[540px] w-full max-w-6xl flex-col items-center justify-center px-4 py-20 text-center md:min-h-[640px] md:px-6">
        <span
          className="lh-fade-up inline-flex items-center gap-2 rounded-full border border-white/20 bg-white/10 px-4 py-1.5 text-xs text-white/90 backdrop-blur"
          style={{ animationDelay: "0.05s" }}
        >
          <span className="flex h-3 w-3 items-center justify-center">
            <span className="h-2 w-2 rounded-full bg-accent-400" />
          </span>
          {t("hero.badge")}
        </span>

        <h1
          className="lh-fade-up mt-6 max-w-3xl font-heading text-3xl font-semibold leading-tight tracking-tight text-white md:text-5xl"
          style={{ animationDelay: "0.12s" }}
        >
          {t("hero.titleLead")}
          <span className="text-accent-400">{t("hero.titleHighlight")}</span>
        </h1>

        <p
          className="lh-fade-up mt-5 max-w-2xl text-sm leading-relaxed text-white/80 md:text-base"
          style={{ animationDelay: "0.2s" }}
        >
          {t("hero.subtitle")}
        </p>

        <div
          className="lh-fade-up mt-9 flex w-full flex-col items-center justify-center gap-3 sm:w-auto sm:flex-row"
          style={{ animationDelay: "0.28s" }}
        >
          <Link
            to="/auth"
            className="inline-flex w-full items-center justify-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-6 py-3 text-sm font-medium text-background-50 transition-colors hover:bg-primary-600 sm:w-auto"
          >
            <span className="flex h-4 w-4 items-center justify-center">
              <i className="ri-login-circle-line" />
            </span>
            {t("hero.login")}
          </Link>
          <Link
            to="/apply"
            className="inline-flex w-full items-center justify-center gap-2 whitespace-nowrap rounded-md border border-white/30 bg-white/5 px-6 py-3 text-sm font-medium text-white backdrop-blur transition-colors hover:bg-white/15 sm:w-auto"
          >
            <span className="flex h-4 w-4 items-center justify-center">
              <i className="ri-file-add-line" />
            </span>
            {t("hero.apply")}
          </Link>
        </div>

        <div
          className="lh-fade-up mt-12 grid w-full max-w-2xl grid-cols-3 gap-3 md:gap-6"
          style={{ animationDelay: "0.36s" }}
        >
          {heroStats.map((item) => (
            <div key={item.label} className="rounded-lg border border-white/15 bg-white/10 px-3 py-4 backdrop-blur">
              <p className="font-heading text-xl font-semibold text-white md:text-2xl">
                {loading ? "--" : formatNumber(item.value)}
              </p>
              <p className="mt-1 text-xs text-white/70 md:text-sm">{item.label}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}